import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

/**
 * Subscribes to realtime INSERT/UPDATE/DELETE on `scenarios` for a given session.
 * Calls `onUpdate` whenever the scenarios change so the parent can reload.
 */
export function useRealtimeScenarios(
  sessionId: string | undefined,
  currentUserId: string | null,
  onUpdate: () => void
) {
  const { toast } = useToast();
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  useEffect(() => {
    if (!sessionId) return;

    const channel = supabase
      .channel(`scenarios_sync_${sessionId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "scenarios",
          filter: `session_id=eq.${sessionId}`,
        },
        (payload) => {
          // Skip own changes
          const changedBy =
            (payload.new as any)?.created_by ||
            (payload.old as any)?.created_by;
          if (changedBy === currentUserId) return;

          const eventType = payload.eventType;
          const name = (payload.new as any)?.name || "Option";

          if (eventType === "INSERT") {
            toast({
              title: "Neue Option erstellt",
              description: `„${name}" wurde von einem Teammitglied hinzugefügt.`,
            });
          } else if (eventType === "UPDATE") {
            toast({
              title: "Option aktualisiert",
              description: `„${name}" wurde von einem Teammitglied bearbeitet.`,
            });
          } else if (eventType === "DELETE") {
            toast({
              title: "Option entfernt",
              description: "Eine Option wurde von einem Teammitglied gelöscht.",
            });
          }

          onUpdateRef.current();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId, currentUserId]);
}
